import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import { Check, Heart, ArrowLeft, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONOGRAM_SAGE = "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/itxixsdb_Monogram_526E3B.png";

const formatDate = (d, lang) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const BankDetails = ({ t }) => (
  <div
    data-testid="grsvp-bank-details"
    className="mt-8 bg-white border border-[var(--border-soft)] p-8 md:p-10"
  >
    <div className="flex flex-col items-center text-center mb-6">
      <Gift className="w-8 h-8 text-[var(--sage-deep)] mb-3" />
      <p className="eyebrow mb-2">{t.details.registry.title}</p>
      <h3 className="font-serif text-2xl md:text-3xl text-[var(--text-primary)]">
        {t.details.registry.privateHeading}
      </h3>
    </div>
    <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed text-center mb-8 font-light">
      {t.details.registry.privateNote}
    </p>

    <div className="grid md:grid-cols-2 gap-6">
      <div
        data-testid="grsvp-bank-mx"
        className="border border-[var(--border-soft)] bg-[var(--ivory)] p-6"
      >
        <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] mb-4">
          <span aria-hidden="true" className="mr-1.5">{t.details.registry.mxFlag}</span>
          {t.details.registry.mxTitle}
        </p>
        <ul className="text-xs text-[var(--text-primary)] space-y-2 leading-relaxed text-left">
          {t.details.registry.mxRows.map((r) => (
            <li key={r.label} className="flex flex-col">
              <span className="text-[var(--text-secondary)] uppercase tracking-wider text-[10px] font-medium">
                {r.label}
              </span>
              <span className="font-medium break-words">{r.value}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        data-testid="grsvp-bank-eu"
        className="border border-[var(--border-soft)] bg-[var(--ivory)] p-6"
      >
        <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] mb-4">
          <span aria-hidden="true" className="mr-1.5">{t.details.registry.euFlag}</span>
          {t.details.registry.euTitle}
        </p>
        <ul className="text-xs text-[var(--text-primary)] space-y-2 leading-relaxed text-left">
          {t.details.registry.euRows.map((r) => (
            <li key={r.label} className="flex flex-col">
              <span className="text-[var(--text-secondary)] uppercase tracking-wider text-[10px] font-medium">
                {r.label}
              </span>
              <span className="font-medium break-words">{r.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const LanguageSwitch = ({ lang, setLang }) => (
  <div className="flex items-center justify-center gap-2 mt-6">
    <button
      data-testid="grsvp-lang-es"
      onClick={() => setLang("es")}
      className={`px-3 py-1 text-[10px] tracking-[0.22em] uppercase border ${
        lang === "es" ? "bg-[var(--sage-deep)] text-white border-[var(--sage-deep)]" : "border-[var(--border-soft)] text-[var(--text-secondary)]"
      }`}
    >
      ES
    </button>
    <button
      data-testid="grsvp-lang-en"
      onClick={() => setLang("en")}
      className={`px-3 py-1 text-[10px] tracking-[0.22em] uppercase border ${
        lang === "en" ? "bg-[var(--sage-deep)] text-white border-[var(--sage-deep)]" : "border-[var(--border-soft)] text-[var(--text-secondary)]"
      }`}
    >
      EN
    </button>
  </div>
);

const GuestRSVP = () => {
  const { t, lang, setLang } = useLanguage();
  const [params] = useSearchParams();
  const code = (params.get("guest") || "").trim();

  const [loading, setLoading] = useState(true);
  const [guest, setGuest] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [attending, setAttending] = useState(true);
  const [confirmedSeats, setConfirmedSeats] = useState(1);
  const [attendeeNames, setAttendeeNames] = useState([""]);
  const [dietary, setDietary] = useState("");
  const [notesFromGuest, setNotesFromGuest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!code) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/guest/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        setGuest(data);
        if (data.language && (data.language === "es" || data.language === "en")) {
          setLang(data.language);
        }
        const isConfirmed = data.rsvp_status === "confirmed";
        const isDeclined = data.rsvp_status === "declined";
        setAttending(!isDeclined);
        const seats = isConfirmed
          ? Math.max(1, data.confirmed_seats || data.reserved_seats)
          : data.reserved_seats;
        setConfirmedSeats(seats);
        const names = Array.from({ length: seats }, (_, i) => data.attendee_names?.[i] || "");
        setAttendeeNames(names);
        setDietary(data.dietary_requirements || "");
        setNotesFromGuest(data.notes_from_guest || "");
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  const updateSeats = (n) => {
    const reserved = guest?.reserved_seats || 1;
    const next = Math.max(1, Math.min(reserved, n));
    setConfirmedSeats(next);
    setAttendeeNames((prev) => {
      const copy = [...prev];
      if (copy.length < next) {
        while (copy.length < next) copy.push("");
      } else {
        copy.length = next;
      }
      return copy;
    });
  };

  const updateName = (i, v) => {
    setAttendeeNames((prev) => {
      const copy = [...prev];
      copy[i] = v;
      return copy;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        attending,
        confirmed_seats: attending ? confirmedSeats : 0,
        attendee_names: attending ? attendeeNames.filter(Boolean) : [],
        dietary_requirements: dietary,
        notes_from_guest: notesFromGuest,
      };
      const { data } = await axios.put(`${API}/guest/${encodeURIComponent(code)}/rsvp`, payload);
      setGuest(data);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setError(t.guestRsvp.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ivory)]">
        <p data-testid="grsvp-loading" className="eyebrow">{t.guestRsvp.loading}</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ivory)] px-6">
        <div data-testid="grsvp-invalid" className="max-w-md w-full text-center bg-white border border-[var(--border-soft)] p-12">
          <img src={MONOGRAM_SAGE} alt="A&E" className="h-14 mx-auto mb-6 object-contain" />
          <h1 className="font-serif text-3xl text-[var(--text-primary)] mb-4">{t.guestRsvp.invalidTitle}</h1>
          <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{t.guestRsvp.invalidBody}</p>
          <Link
            to="/"
            data-testid="grsvp-back-home"
            className="inline-flex items-center gap-2 mt-8 text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] hover:text-[var(--text-primary)]"
          >
            <ArrowLeft className="w-3 h-3" /> {t.guestRsvp.backHome}
          </Link>
          <LanguageSwitch lang={lang} setLang={setLang} />
        </div>
      </div>
    );
  }

  if (!guest.link_active) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ivory)] px-6">
        <div data-testid="grsvp-expired" className="max-w-md w-full text-center bg-white border border-[var(--border-soft)] p-12">
          <img src={MONOGRAM_SAGE} alt="A&E" className="h-14 mx-auto mb-6 object-contain" />
          <Heart className="w-7 h-7 text-[var(--sage-deep)] mx-auto mb-5" />
          <h1 className="font-serif text-3xl text-[var(--text-primary)] mb-4">{t.guestRsvp.expiredTitle}</h1>
          <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{t.guestRsvp.expiredBody}</p>
          <Link
            to="/"
            data-testid="grsvp-expired-home"
            className="inline-flex items-center gap-2 mt-8 text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] hover:text-[var(--text-primary)]"
          >
            <ArrowLeft className="w-3 h-3" /> {t.guestRsvp.backHome}
          </Link>
          <LanguageSwitch lang={lang} setLang={setLang} />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ivory)] px-6 py-16">
        <Reveal>
          <div className="max-w-3xl w-full">
            <div data-testid="grsvp-success" className="text-center bg-white border border-[var(--border-soft)] p-12 md:p-16">
              <div className="w-16 h-16 rounded-full bg-[var(--sage-soft)] mx-auto flex items-center justify-center mb-6">
                {guest.rsvp_status === "confirmed" ? (
                  <Check className="w-7 h-7 text-[var(--sage-deep)]" />
                ) : (
                  <Heart className="w-7 h-7 text-[var(--sage-deep)]" />
                )}
              </div>
              <h1 className="font-serif text-3xl md:text-4xl text-[var(--text-primary)] mb-5">
                {t.guestRsvp.successTitle}
              </h1>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                {guest.rsvp_status === "confirmed" ? t.guestRsvp.successBody : t.guestRsvp.successDeclined}
              </p>
              <Link
                to="/"
                data-testid="grsvp-back"
                className="inline-flex items-center gap-2 mt-10 text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] hover:text-[var(--text-primary)]"
              >
                <ArrowLeft className="w-3 h-3" /> {t.guestRsvp.backHome}
              </Link>
              <LanguageSwitch lang={lang} setLang={setLang} />
            </div>
            {guest.rsvp_status === "confirmed" && <BankDetails t={t} />}
          </div>
        </Reveal>
      </div>
    );
  }

  const fullName = `${guest.first_name}${guest.last_name ? " " + guest.last_name : ""}`.trim();
  const reserved = guest.reserved_seats;
  const reservedText = (reserved === 1 ? t.guestRsvp.reservedOne : t.guestRsvp.reservedMany).replace("{n}", reserved);
  const alreadyResponded = guest.rsvp_status !== "pending";

  return (
    <div className="min-h-screen bg-[var(--ivory)] px-4 md:px-6 py-10 md:py-16">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src={MONOGRAM_SAGE} alt="A&E" className="h-12 md:h-14 mx-auto object-contain" />
          </Link>
          <LanguageSwitch lang={lang} setLang={setLang} />
        </div>

        <Reveal>
          <div className="bg-white border border-[var(--border-soft)] p-8 md:p-14">
            <div className="text-center mb-10">
              <p className="eyebrow mb-4">RSVP</p>
              <h1 data-testid="grsvp-welcome" className="font-serif text-3xl md:text-4xl text-[var(--text-primary)] mb-3">
                {t.guestRsvp.welcome.replace("{name}", fullName)}
              </h1>
              <p data-testid="grsvp-reserved" className="text-[var(--text-secondary)] text-sm md:text-base">
                {reservedText}
              </p>
              {guest.rsvp_open && alreadyResponded && (
                <p data-testid="grsvp-already" className="mt-4 text-[12px] italic text-[var(--sage-deep)]">
                  {(guest.rsvp_status === "confirmed" ? t.guestRsvp.alreadyConfirmed : t.guestRsvp.alreadyDeclined).replace(
                    "{date}",
                    formatDate(guest.response_date, lang),
                  )}
                </p>
              )}
            </div>

            {!guest.rsvp_open ? (
              <div data-testid="grsvp-closed" className="text-center border-t border-[var(--border-soft)] pt-8">
                <p className="eyebrow mb-3">{t.guestRsvp.closedNotice}</p>
                <p data-testid="grsvp-closed-status" className="text-[var(--text-secondary)] text-sm md:text-base leading-relaxed">
                  {guest.rsvp_status === "confirmed"
                    ? t.guestRsvp.closedConfirmed.replace("{n}", guest.confirmed_seats)
                    : guest.rsvp_status === "declined"
                    ? t.guestRsvp.closedDeclined
                    : t.guestRsvp.closedPending}
                </p>
              </div>
            ) : (
            <form onSubmit={submit} data-testid="grsvp-form" className="space-y-8">
              <div>
                <label className="luxe-label">{t.guestRsvp.questionAttend}</label>
                <div className="flex gap-3 mt-2">
                  <button
                    data-testid="grsvp-yes"
                    type="button"
                    onClick={() => setAttending(true)}
                    className={`flex-1 py-4 px-4 text-[11px] tracking-[0.22em] uppercase border transition-all ${
                      attending
                        ? "bg-[var(--sage-deep)] text-white border-[var(--sage-deep)]"
                        : "border-[var(--border-soft)] text-[var(--text-primary)] hover:border-[var(--sage-deep)]"
                    }`}
                  >
                    {t.guestRsvp.yes}
                  </button>
                  <button
                    data-testid="grsvp-no"
                    type="button"
                    onClick={() => setAttending(false)}
                    className={`flex-1 py-4 px-4 text-[11px] tracking-[0.22em] uppercase border transition-all ${
                      !attending
                        ? "bg-[var(--text-primary)] text-white border-[var(--text-primary)]"
                        : "border-[var(--border-soft)] text-[var(--text-primary)] hover:border-[var(--text-primary)]"
                    }`}
                  >
                    {t.guestRsvp.no}
                  </button>
                </div>
              </div>

              {attending && reserved > 1 && (
                <div>
                  <label className="luxe-label">{t.guestRsvp.attendingCount}</label>
                  <div className="flex items-center gap-4 mt-2">
                    <button
                      data-testid="grsvp-seats-minus"
                      type="button"
                      onClick={() => updateSeats(confirmedSeats - 1)}
                      className="w-10 h-10 border border-[var(--border-soft)] text-[var(--text-primary)] hover:border-[var(--sage-deep)]"
                    >
                      −
                    </button>
                    <span data-testid="grsvp-seats-value" className="font-serif text-3xl tabular-nums w-12 text-center">
                      {confirmedSeats}
                    </span>
                    <button
                      data-testid="grsvp-seats-plus"
                      type="button"
                      onClick={() => updateSeats(confirmedSeats + 1)}
                      className="w-10 h-10 border border-[var(--border-soft)] text-[var(--text-primary)] hover:border-[var(--sage-deep)]"
                    >
                      +
                    </button>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--text-secondary)] ml-2">
                      / {reserved}
                    </span>
                  </div>
                  <p className="text-[11px] text-[var(--text-secondary)] mt-2 italic">
                    {t.guestRsvp.maxSeatsHint.replace("{n}", reserved)}
                  </p>
                </div>
              )}

              {attending && (
                <div>
                  <label className="luxe-label">{t.guestRsvp.attendeeNames}</label>
                  <div className="space-y-3 mt-2">
                    {attendeeNames.map((n, i) => (
                      <input
                        key={i}
                        data-testid={`grsvp-name-${i}`}
                        className="luxe-input"
                        placeholder={t.guestRsvp.attendeeNamePlaceholder.replace("{n}", i + 1)}
                        value={n}
                        onChange={(e) => updateName(i, e.target.value)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {attending && (
                <div>
                  <label className="luxe-label">{t.guestRsvp.dietary}</label>
                  <input
                    data-testid="grsvp-dietary"
                    className="luxe-input"
                    value={dietary}
                    onChange={(e) => setDietary(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="luxe-label">{t.guestRsvp.notes}</label>
                <textarea
                  data-testid="grsvp-notes"
                  rows={3}
                  className="luxe-input resize-none"
                  placeholder={t.guestRsvp.notesPlaceholder}
                  value={notesFromGuest}
                  onChange={(e) => setNotesFromGuest(e.target.value)}
                />
              </div>

              {error && (
                <p data-testid="grsvp-error" className="text-red-600 text-sm">{error}</p>
              )}

              <div className="flex justify-center pt-4">
                <button data-testid="grsvp-submit" type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? t.guestRsvp.submitting : alreadyResponded ? t.guestRsvp.update : t.guestRsvp.submit}
                </button>
              </div>
            </form>
            )}
          </div>
        </Reveal>

        <BankDetails t={t} />

        <div className="text-center mt-8">
          <Link
            to="/"
            data-testid="grsvp-home"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--text-secondary)] hover:text-[var(--sage-deep)]"
          >
            <ArrowLeft className="w-3 h-3" /> {t.guestRsvp.backHome}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GuestRSVP;
